import { BrowserModule } from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HttpClientModule } from "@angular/common/http";

import { NZ_I18N, en_US, it_IT } from "ng-zorro-antd/i18n";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { LoginComponent } from "./pages/login/login.component";
import { HttpClient } from "@angular/common/http";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { registerLocaleData } from "@angular/common";
import it from "@angular/common/locales/it";
import { AuthService } from "./services/auth.service";
import { AuthClienteService } from "./services/authCliente.service";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { AuthInterceptor } from "./interceptors/auth.interceptor";
import { AuthGuard } from "./guards/auth.guard";
import { NgZorroModule } from "./ng-zorro.module";
import { RecoverComponent } from "./pages/recover/recover.component";
registerLocaleData(it);

// FIRESTORE MODULES
import { AngularFireModule } from "@angular/fire/compat";
import {
  SETTINGS as FIRESTORE_SETTINGS,
  ENABLE_PERSISTENCE,
  PERSISTENCE_SETTINGS,
} from "@angular/fire/compat/firestore";
import { firebaseConfig } from "./firestore.config";

import { ScrollingModule } from "@angular/cdk/scrolling";
import { CfValidatorDirective } from "./services/validator.cf";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { NzIconModule } from "ng-zorro-antd/icon";
import { APP_ICONS } from "./icons-used";
import { NewCompilationComponent } from "./pages/new-compilation/new-compilation.component";
import { PolicyApprovementComponent } from './pages/policy-approvement/policy-approvement.component';
import { LoginShellModule } from "./shared/components/login-shell/login-shell.module";
import { FabModule } from "./shared/components/fab/fab.module";

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

// iPadOS si presenta come "Macintosh" ma è touch: serve il secondo check.
export function isIosStandalonePwa(): boolean {
  const ua = navigator.userAgent;
  const isIos = /iP(hone|ad|od)/.test(ua)
    || (/Macintosh/.test(ua) && "ontouchend" in document);
  const isStandalone = (navigator as any).standalone === true
    || (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  return isIos && isStandalone;
}

// Solo le icone realmente usate (vedi icons-used.ts, auto-generato): l'intero
// pacchetto AllIcons pesava ~3MB nel bundle iniziale e azzerava il tree-shaking.
const icons = APP_ICONS;

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    RecoverComponent,
    CfValidatorDirective, NewCompilationComponent, PolicyApprovementComponent
  ],
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader, // exported factory function needed for AoT compilation
        deps: [HttpClient],
      },
    }),
    NzIconModule.forRoot(icons),
    BrowserModule,
    AppRoutingModule,
    NgZorroModule,
    ScrollingModule,
    FormsModule,
    HttpClientModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    AngularFireModule.initializeApp(firebaseConfig),
    LoginShellModule,
    FabModule
  ],
  exports: [NgZorroModule, ScrollingModule, TranslateModule],
  providers: [
    AuthService,
    AuthGuard,
    // Nel root injector: i guard eager (LoginClienteGuard, AuthClienteGuard in
    // app-routing) lo iniettano PRIMA che l'area cliente lazy sia caricata.
    AuthClienteService,
    { provide: NZ_I18N, useValue: it_IT },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // iOS/Safari: il WebChannel di default spesso non si connette → i listener
    // Firestore restano appesi e le pagine restano in caricamento.
    // `experimentalAutoDetectLongPolling` NON basta: su Safari/WebKit la fase di
    // detection tenta prima lo streaming WebChannel e, sui documenti pesanti (es.
    // `questionari/{q}/versioni/{v}`), STALLA ~30s prima di ripiegare su long-polling
    // → il bottone "Iniziamo" della compilazione si abilitava dopo ~33s.
    // Misurato su motore WebKit: read versione 30.300ms → 1.630ms forzando il
    // long-polling (salta la detection). autoDetect va messo a false esplicito,
    // altrimenti AngularFire lo reimposta a true e Firestore lancia invalid-argument.
    { provide: FIRESTORE_SETTINGS, useValue: { experimentalForceLongPolling: true, experimentalAutoDetectLongPolling: false, merge: true, cacheSizeBytes: 50 * 1024 * 1024 } },
    // Cache IndexedDB: navigazioni e reload servono i dati già visti dalla cache
    // locale (meno read fatturati, render quasi istantaneo offline-first).
    // synchronizeTabs evita il fallimento di enablePersistence con più tab aperte.
    // ECCEZIONE iOS PWA standalone: bug WebKit — indexedDB.open() può non
    // risolvere mai al primo avvio/resume → enablePersistence appeso → tutte le
    // query Firestore in coda → spinner infinito. Lì persistence disattivata.
    { provide: ENABLE_PERSISTENCE, useValue: !isIosStandalonePwa() },
    { provide: PERSISTENCE_SETTINGS, useValue: { synchronizeTabs: true } },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
